import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Checkbox from '../components/checkbox';
import DropDown from '../components/McDropdown';
import { initModal } from '../constant/InitialData';
import { OrganisationContext } from '../context/organisationContext';
import { getItemFromLocalStorage } from '../helpers/localstorage';
import { DEFAULT_FORMAT, PAPER_FORMATS } from '../pages/template-editor/paper';
import { createTemplate, getTemplateVersions } from '../store/features/templatesSlice';
import { addToast } from '../store/features/toastSlice';
import CommonPopup from './common-popup';
import { AddFormWrapper } from './modal.styled';

const FORMAT_OPTIONS = PAPER_FORMATS.map(format => ({ id: format, name: format }));

/**
 * Creating a template also creates its first, unpublished version server-side, so this drops the
 * user straight into the editor for it.
 */
const AddTemplate = () => {
  const { setModal, modal } = useContext(OrganisationContext);
  const { onSuccess = () => {} } = modal.content;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [template, setTemplate] = useState({
    format: FORMAT_OPTIONS.find(option => option.id === DEFAULT_FORMAT),
    landscape: false,
  });
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);

  const checkErrors = () => {
    if (!template?.name?.trim()) {
      setError({ name: true });
      dispatch(addToast({ error: true, text: 'Please enter a template name' }));
      return true;
    }
    return false;
  };

  /** The create response is a summary without version ids, so the version has to be looked up. */
  const openFirstVersion = async templateId => {
    try {
      const versions = await dispatch(
        getTemplateVersions({ id: templateId, params: { page: 0, size: 1, sort_by: 'version_no', order_by: 'ASC' } }),
      );
      const firstVersion = versions?.content?.[0];
      navigate(
        firstVersion ? `/templates/${templateId}/versions/${firstVersion.id}` : `/templates/${templateId}/versions`,
      );
    } catch (e) {
      navigate(`/templates/${templateId}/versions`);
    }
  };

  const onConfirmAction = () => {
    if (loading || checkErrors()) {
      return;
    }
    const orgId = getItemFromLocalStorage('user')?.organizations?.[0]?.id;
    setLoading(true);

    const request = {
      name: template.name.trim(),
      description: template.description?.trim() || null,
      format: template.format?.id || DEFAULT_FORMAT,
      landscape: template.landscape,
      ...(orgId ? { organizations: [orgId] } : {}),
    };

    dispatch(createTemplate({ request }))
      .then(created => {
        onSuccess();
        setModal(initModal);
        dispatch(addToast({ error: false, title: 'Add template', text: 'Template has been created' }));
        if (created?.id) {
          openFirstVersion(created.id);
        }
      })
      .catch(() => {
        setLoading(false);
        dispatch(addToast({ error: true, text: 'Template has not been created' }));
      });
  };

  return (
    <div>
      <CommonPopup
        popupTitle="Add template"
        confirmButtonProps={{ label: 'Add' }}
        disabled={loading}
        onCancel={() => setModal(initModal)}
        onConfirm={onConfirmAction}>
        <AddFormWrapper className="flex-column items-center w-full">
          <div className="w-full flex-column mt-6">
            <label className="regular-text lighter-text mb-1 font-12">{t('NAME')}</label>
            <input
              autoComplete="turnoff"
              className={`input ${error?.name && 'error-border'}`}
              onChange={e => {
                setTemplate({ ...template, name: e.target.value });
                setError({ ...error, name: false });
              }}
              placeholder="Template name"
              value={template?.name || ''}
            />
          </div>

          <div className="w-full flex-column mt-6">
            <label className="regular-text lighter-text mb-1 font-12">{t('DESCRIPTION')}</label>
            <textarea
              className="input description-input"
              onChange={e => setTemplate({ ...template, description: e.target.value })}
              placeholder="Enter description"
              rows={3}
              value={template?.description || ''}
            />
          </div>

          <div className="w-full flex-column mt-6">
            <label className="regular-text lighter-text mb-1 font-12">Page format</label>
            <DropDown
              options={FORMAT_OPTIONS}
              selected={template?.format}
              setSelected={option => setTemplate({ ...template, format: option })}
              placeholder="Select"
            />
          </div>

          <div className="w-full flex items-center mt-6">
            <Checkbox
              checked={template?.landscape}
              onChange={checked => setTemplate({ ...template, landscape: checked })}
            />
            <label className="regular-text ml-2">Landscape</label>
          </div>
        </AddFormWrapper>
      </CommonPopup>
    </div>
  );
};

export default AddTemplate;
